import { observer } from "mobx-react";
import type React from "react";
import { useMemo } from "react";
import { Navigate, matchPath, useLocation, useNavigate } from "react-router-dom";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import {
  PRODUCT_PAGES_ROUTES,
  RESULT_PAGE_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEACHER_INSIGHT_PAGE_ROUTES,
  TEST_PAGE_ROUTES,
} from "constants/routes";

import { useProduct } from "hooks/useProduct";

import { ProductsDropdown } from "components/ProductsDropdown/ProductsDropdown";
import { useProfile } from "hooks/useProfile";
import { type UserLocalStorageDictionary, useSelectedProductId } from "hooks/useSelectedProductId";
import { StyledIcon, StyledProductSelectorItem } from "./InsightProductSelector.styled";

interface InsightProductSelectorProps {
  selectedProductId: number;
}

const productDependantRoutes = [
  TEACHER_HOME_PAGE_ROUTES.TEACHER_INSTRUCTIONS,
  TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_OVERVIEW,
  TEACHER_INSIGHT_PAGE_ROUTES.WRITING_RESULTS_FOR_ACCOUNT,
  TEACHER_INSIGHT_PAGE_ROUTES.WRITING_SKILLS,
  TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_FOR_ACCOUNT,
  PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS,
  PRODUCT_PAGES_ROUTES.PRODUCT_LIST,
  PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS,
  PRODUCT_PAGES_ROUTES.PRODUCT_TOPIC_DETAILS,
  PRODUCT_PAGES_ROUTES.PRODUCT_WRITING_MODULE_DETAILS,
  PRODUCT_PAGES_ROUTES.EXPIRED_LICENSE,
  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS,
  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST,
  TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS,
];

export const InsightProductSelector: React.FC<InsightProductSelectorProps> = observer(
  ({ selectedProductId }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { productsList } = useProduct();
    const { userDetails } = useProfile();

    // If current path is dependant on the selected product, calculate
    // url to which the user will be redirected when changes their selection
    const redirectPath = useMemo(() => {
      // Certificates page will match the path `/products/:productId`, but we this page
      // is not product specific, so showing the selector on it doesn't add any value
      if (location.pathname === RESULT_PAGE_ROUTES.OBTAINED_CERTIFICATES) {
        return null;
      }

      let i = 0;
      while (i < productDependantRoutes.length) {
        const currentRouteMatches = !!matchPath(
          { path: productDependantRoutes[i], end: true },
          location.pathname,
        );

        if (currentRouteMatches) {
          switch (productDependantRoutes[i]) {
            // If user was viewing results for a specific student, redirect to overview
            //  (because the student may not have license on the newly selected product)
            case TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_FOR_ACCOUNT:
              return TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_OVERVIEW;
            case TEACHER_INSIGHT_PAGE_ROUTES.WRITING_RESULTS_FOR_ACCOUNT:
              return TEACHER_INSIGHT_PAGE_ROUTES.WRITING_SKILLS;
            // If user was in module specific path, redirect to student dashboard
            //  (because most likely the newly selected product won't have the same content)
            // Also, if user was on license expired page, we want to go to student
            //  dashboard when a new one is selected (the other may not be expired)
            case PRODUCT_PAGES_ROUTES.PRODUCT_MODULE_DETAILS:
            case PRODUCT_PAGES_ROUTES.PRODUCT_WRITING_MODULE_DETAILS:
            case PRODUCT_PAGES_ROUTES.PRODUCT_TOPIC_DETAILS:
            case TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_GROUPS:
            case TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_LIST:
            case TEST_PAGE_ROUTES.DIAGNOSTIC_TEST_DETAILS:
            case PRODUCT_PAGES_ROUTES.EXPIRED_LICENSE:
              return PRODUCT_PAGES_ROUTES.PRODUCT_DETAILS;
            // For any other product related path, user can remain in same page
            default:
              return productDependantRoutes[i];
          }
        }

        i += 1;
      }

      return null;
    }, [location.pathname]);

    // Replace product id in current path
    const onSelectProduct = (productId: number) => {
      const preferencesPerProduct: UserLocalStorageDictionary = JSON.parse(
        localStorage.getItem("selectedProduct") ?? "{}",
      );
      if (userDetails) {
        preferencesPerProduct[userDetails.id] = {
          ...preferencesPerProduct[productId],
          lastSelectedProductId: productId,
        };
        localStorage.setItem("selectedProduct", JSON.stringify(preferencesPerProduct));
      }

      // If there's a redirect path set, go to that page with the newly selected product id
      if (redirectPath) {
        navigate(buildUrlWithPathParams(redirectPath, { productId }));
      }
    };

    const selectedProductFromStorage = useSelectedProductId() || Number.NaN;

    if (productsList?.length === 0) {
      return <Navigate to={PRODUCT_PAGES_ROUTES.PRODUCT_LIST} />;
    }

    if (redirectPath && productsList && productsList.length > 1) {
      return (
        <StyledProductSelectorItem>
          <StyledIcon iconName="far fa-dot-circle" iconSize="lg" />
          <ProductsDropdown
            products={productsList}
            selectedProductId={
              isNaN(selectedProductFromStorage) ? +selectedProductId : selectedProductFromStorage
            }
            onSelectProduct={onSelectProduct}
          />
        </StyledProductSelectorItem>
      );
    }
    return null;
  },
);
